/**
 * useConversationHistory Hook
 *
 * Fetches past mission executions from orchestration-service and maps them
 * to ConversationHistoryEntry[] for display in the sidebar.
 * Replaces the old chat_sessions-based approach (table was never written to).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { executionsApi, type ExecutionListScope, type ExecutionResponse } from '../services/api'
import type { ChatMode, ConversationHistoryEntry, MissionConversationGroup } from '../types/chat'
import type { Mission } from '../types/mission'
import { useMissionStore } from './useMissions'

/**
 * Format a timestamp into a short display string for execution entries.
 * E.g. "Mar 2, 00:15"
 */
export function formatExecutionTitle(date: Date): string {
  const month = date.toLocaleString('en-US', { month: 'short' })
  const day = date.getDate()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${month} ${day}, ${hours}:${minutes}`
}

/**
 * Run statuses a mission's run list shows: live runs and every ended run,
 * including runs stopped with the Stop button (`cancelled`, agion/Agion#2077).
 * `pending` and `paused` runs stay out of the list until the owner decides
 * how they read there.
 */
const LISTED_RUN_STATUSES: ReadonlySet<string> = new Set(['running', 'completed', 'failed', 'cancelled'])

export function isListedRunStatus(status: string): boolean {
  return LISTED_RUN_STATUSES.has(status)
}

/** One run as a history entry; the per-mission run list maps runs the same way. */
export function toConversationHistoryEntry(
  execution: ExecutionResponse,
  mission: Pick<Mission, 'name' | 'hostId'> | undefined,
): ConversationHistoryEntry {
  const timestamp = execution.completed_at || execution.started_at || execution.created_at
  return {
    id: execution.id,
    title: mission?.name || 'Mission Execution',
    mode: 'mission' as ChatMode,
    lastActivityAt: new Date(timestamp),
    missionId: execution.mission_id,
    missionName: mission?.name,
    // Each run's own recorded host, else the mission's (agion/Agion#2485).
    hostId: execution.edge_host_id || mission?.hostId,
    isActive: execution.status === 'running',
    status: execution.status,
  }
}

interface UseConversationHistoryReturn {
  entries: ConversationHistoryEntry[]
  groupedByMission: MissionConversationGroup[]
  groupedByMode: Partial<Record<ChatMode, ConversationHistoryEntry[]>>
  isLoading: boolean
  error: string | null
  /** The failed read itself, for screens that word it from its typed fields (ADR-20260925). */
  failure: unknown
  refresh: () => Promise<void>
}

export interface UseConversationHistoryOptions {
  /** The server-side scope of the run list; the new chat's Recent asks for own and invited runs. */
  scope?: ExecutionListScope
}

export function useConversationHistory(options: UseConversationHistoryOptions = {}): UseConversationHistoryReturn {
  const { scope } = options
  const [rawExecutions, setRawExecutions] = useState<ExecutionResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  const getMissionById = useMissionStore((s) => s.getMissionById)
  // Subscribe to missions array so entries recompute when missions load
  const missions = useMissionStore((s) => s.missions)

  const fetchExecutions = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    setFailure(null)
    try {
      const response = scope ? await executionsApi.listExecutions(50, 0, scope) : await executionsApi.listExecutions(50, 0)
      const filtered = (response.executions ?? []).filter(e => isListedRunStatus(e.status))
      setRawExecutions(filtered)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load executions')
      setFailure(err)
      setRawExecutions([])
    } finally {
      setIsLoading(false)
    }
  }, [scope])

  useEffect(() => {
    fetchExecutions()
  }, [fetchExecutions])

  // Enrich executions with mission names (separate from fetch to avoid re-fetch loops)
  const entries = useMemo(() => {
    return rawExecutions
      .map((execution) => toConversationHistoryEntry(execution, getMissionById(execution.mission_id)))
      .sort((a, b) => b.lastActivityAt.getTime() - a.lastActivityAt.getTime())
  // eslint-disable-next-line react-hooks/exhaustive-deps -- missions triggers recompute when store loads
  }, [rawExecutions, getMissionById, missions])

  // Group executions by parent mission
  const groupedByMission = useMemo(() => {
    const groups = new Map<string, MissionConversationGroup>()
    for (const entry of entries) {
      const key = entry.missionId || entry.id
      const existing = groups.get(key)
      if (existing) {
        existing.executions.push(entry)
        if (entry.isActive) existing.hasActive = true
        if (entry.lastActivityAt > existing.latestActivityAt) {
          existing.latestActivityAt = entry.lastActivityAt
        }
      } else {
        groups.set(key, {
          missionId: key,
          missionName: entry.missionName || entry.title,
          hostId: entry.hostId,
          executions: [entry],
          hasActive: !!entry.isActive,
          latestActivityAt: entry.lastActivityAt,
        })
      }
    }
    return Array.from(groups.values())
      .sort((a, b) => {
        if (a.hasActive !== b.hasActive) return a.hasActive ? -1 : 1
        return b.latestActivityAt.getTime() - a.latestActivityAt.getTime()
      })
  }, [entries])

  const groupedByMode = useMemo(() => {
    const groups: Partial<Record<ChatMode, ConversationHistoryEntry[]>> = {}
    for (const entry of entries) {
      if (!groups[entry.mode]) {
        groups[entry.mode] = []
      }
      groups[entry.mode]!.push(entry)
    }
    return groups
  }, [entries])

  return {
    entries,
    groupedByMission,
    groupedByMode,
    isLoading,
    error,
    failure,
    refresh: fetchExecutions,
  }
}
